/**
 * Offline Stories Management Hook
 *
 * Manages saving stories for offline reading using the Cache API.
 * Offline story saves are available to everyone.
 */

import { useState, useEffect, useCallback } from 'react'

// Match the service worker navigation cache so saved read pages are usable offline.
const CACHE_NAME = 'pages'
const STORAGE_KEY = 'offline-story-slugs'

export interface OfflineStoriesState {
  savedSlugs: string[]
  isLoading: boolean
  error: string | null
}

interface SavedStoriesData {
  slugs: string[]
  savedAt: Record<string, number>
}

function getStoryRouteUrl(slug: string, locale: string): string {
  return `/${locale}/read/${slug}`
}

function getLegacyStoryJsonUrls(slug: string, locale: string): string[] {
  return [
    `/content/stories/${slug}.${locale}.json`,
    `/content/stories/core/${slug}.json`,
    `/content/stories/locales/${locale}/${slug}.json`
  ]
}

function getStorageData(): SavedStoriesData {
  if (typeof window === 'undefined') return { slugs: [], savedAt: {} }
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return { slugs: [], savedAt: {} }
    return JSON.parse(raw)
  } catch {
    return { slugs: [], savedAt: {} }
  }
}

function setStorageData(data: SavedStoriesData): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
  } catch (e) {
    console.error('Failed to save offline stories data:', e)
  }
}

/**
 * Hook for managing offline stories
 * @param locale - Current locale (e.g., 'en')
 */
export function useOfflineStories(locale: string = 'en') {
  const [savedSlugs, setSavedSlugs] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Load saved slugs on mount
  useEffect(() => {
    const data = getStorageData()
    setSavedSlugs(data.slugs)
  }, [])

  // Check if a story is saved
  const isStorySaved = useCallback(
    (slug: string): boolean => {
      return savedSlugs.includes(slug)
    },
    [savedSlugs]
  )

  // Save a story for offline use
  const saveStory = useCallback(
    async (slug: string): Promise<boolean> => {
      if (isStorySaved(slug)) return true
      setIsSaving(slug)
      setError(null)

      try {
        const storyRouteUrl = getStoryRouteUrl(slug, locale)

        const cache = await caches.open(CACHE_NAME)
        const response = await fetch(storyRouteUrl)

        if (!response.ok) {
          throw new Error(
            `Failed to cache ${storyRouteUrl}: ${response.status}`
          )
        }

        await cache.put(storyRouteUrl, response.clone())

        // Update storage
        const data = getStorageData()
        data.slugs = [...new Set([...data.slugs, slug])]
        data.savedAt[slug] = Date.now()
        setStorageData(data)
        setSavedSlugs(data.slugs)

        return true
      } catch (e) {
        console.error('Failed to save story offline:', e)
        setError('Failed to save story offline')
        return false
      } finally {
        setIsSaving(null)
      }
    },
    [isStorySaved, locale]
  )

  // Remove a story from offline storage
  const removeStory = useCallback(
    async (slug: string): Promise<boolean> => {
      setIsLoading(true)
      setError(null)

      try {
        // Get URLs to remove
        const data = getStorageData()
        const urlsToRemove = [
          getStoryRouteUrl(slug, locale),
          ...getLegacyStoryJsonUrls(slug, locale)
        ]

        // Remove from cache
        const cache = await caches.open(CACHE_NAME)
        await Promise.all(
          urlsToRemove.map((url) => cache.delete(url).catch(() => {}))
        )

        // Update storage
        data.slugs = data.slugs.filter((s) => s !== slug)
        delete data.savedAt[slug]
        setStorageData(data)
        setSavedSlugs(data.slugs)

        return true
      } catch (e) {
        console.error('Failed to remove story:', e)
        setError('Failed to remove story')
        return false
      } finally {
        setIsLoading(false)
      }
    },
    [locale]
  )

  // Silently update a saved story in background (called when viewing online)
  const refreshStory = useCallback(
    async (slug: string): Promise<void> => {
      if (!isStorySaved(slug)) return

      try {
        const storyRouteUrl = getStoryRouteUrl(slug, locale)
        const cache = await caches.open(CACHE_NAME)
        const response = await fetch(storyRouteUrl)
        if (!response.ok) return

        await cache.put(storyRouteUrl, response.clone())
        // Update timestamp
        const data = getStorageData()
        data.savedAt[slug] = Date.now()
        setStorageData(data)
      } catch {
        // Silent fail - user doesn't need to know about refresh failures
      }
    },
    [isStorySaved, locale]
  )

  // Load the cached story route response for offline use.
  const getOfflineStory = useCallback(
    async (slug: string): Promise<Response | null> => {
      try {
        const cache = await caches.open(CACHE_NAME)
        return (await cache.match(getStoryRouteUrl(slug, locale))) ?? null
      } catch {
        return null
      }
    },
    [locale]
  )

  return {
    // State
    savedSlugs,
    isLoading,
    isSaving,
    error,
    // Checks
    isStorySaved,

    // Actions
    saveStory,
    removeStory,
    refreshStory,
    getOfflineStory
  }
}
